import {useState} from "react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button.tsx";
import {XMarkIcon} from "@heroicons/react/24/outline";
import {toast} from "sonner";
import {UserSearchCombobox} from "@/features/user-directory/components/UserSearchCombobox";
import {LearningUserSearchResult} from "@/features/user-directory/types";
import {ChipDetail} from "@/types/chipAssignment";
import {useUnassignChipMutation} from "@/store/chipAssignmentApi.ts";
import {UnassignConfirmDialog} from "./UnassignConfirmDialog";

interface ChipResultCardProps {
    chip: ChipDetail;
    onSelectUserToAssign: (user: LearningUserSearchResult) => void;
    onCreateUser: () => void;
    disabled?: boolean;
}

// הצגת פרטי Chip + המשתמש המשויך (אם יש) + חיפוש/בחירת learning.User לשיוך.
export function ChipResultCard({chip, onSelectUserToAssign, onCreateUser, disabled}: ChipResultCardProps) {
    const [unassignChip, {isLoading: isUnassigning}] = useUnassignChipMutation();
    const [confirmUnassignOpen, setConfirmUnassignOpen] = useState(false);

    async function handleConfirmUnassign() {
        try {
            await unassignChip({chip_id: chip.id, user_id: chip.assigned_user?.id}).unwrap();
            toast.success("שיוך הכרטיס נותק בהצלחה");
            setConfirmUnassignOpen(false);
        } catch (err: unknown) {
            const msg = (err as {data?: {msg?: string}})?.data?.msg || "שגיאה בניתוק השיוך";
            toast.error(msg);
        }
    }

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle className="text-lg">כרטיס {chip.internal_code}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-base">
                    <div><span className="text-muted-foreground">קוד חיצוני: </span>{chip.external_number || '—'}</div>
                    <div><span className="text-muted-foreground">מספר סידורי: </span>{chip.serial_number ?? '—'}</div>
                    <div><span className="text-muted-foreground">ת.ז.: </span>{chip.tz || '—'}</div>
                    <div><span className="text-muted-foreground">טלפונים: </span>{(chip.phones || []).join(', ') || '—'}</div>
                    <div>
                        <span className="text-muted-foreground">סטטוס: </span>
                        <Badge variant={chip.is_active ? "default" : "secondary"}>
                            {chip.is_active ? 'פעיל' : 'לא פעיל'}
                        </Badge>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="text-base font-medium">משתמש משויך</div>
                    {chip.assigned_user ? (
                        <Badge variant="secondary" className="text-sm gap-1 pe-1">
                            {chip.assigned_user.name || 'ללא שם'}
                            <button
                                type="button"
                                title="נתק שיוך"
                                onClick={() => setConfirmUnassignOpen(true)}
                                disabled={disabled}
                                className="rounded-full hover:bg-black/10 p-0.5 disabled:opacity-50"
                            >
                                <XMarkIcon className="h-3 w-3" />
                            </button>
                        </Badge>
                    ) : (
                        <p className="text-sm text-muted-foreground">כרטיס זה אינו משויך למשתמש</p>
                    )}
                </div>

                <div className="space-y-2">
                    <div className="text-base font-medium">שיוך משתמש לכרטיס זה</div>
                    <UserSearchCombobox onSelect={onSelectUserToAssign} disabled={disabled} />
                    <Button type="button" variant="outline" className="text-base" onClick={onCreateUser} disabled={disabled}>
                        + צור משתמש חדש ושייך
                    </Button>
                </div>

                <UnassignConfirmDialog
                    open={confirmUnassignOpen}
                    chipLabel={chip.internal_code}
                    chipExternalCode={chip.external_number ?? null}
                    isLoading={isUnassigning}
                    onCancel={() => setConfirmUnassignOpen(false)}
                    onConfirm={handleConfirmUnassign}
                />
            </CardContent>
        </Card>
    );
}
